import React from 'react';
import { motion } from 'framer-motion';
import { FileCheck, AlertCircle, Shield, Scale, RefreshCw, Mail, CreditCard } from 'lucide-react';
import SEO from '../components/SEO';

// V5 Design Constants
const v5 = {
  colors: {
    navy: '#1e1b4b',
    cream: '#FEF7ED',
    white: '#FFFFFF',
    purple: '#8b5cf6',
    purpleDark: '#7c3aed',
    orange: '#f97316',
    teal: '#14b8a6',
    amber: '#f59e0b',
    red: '#ef4444',
    textPrimary: '#1e1b4b',
    textSecondary: 'rgba(30, 27, 75, 0.7)',
    textMuted: 'rgba(30, 27, 75, 0.5)',
    textHint: 'rgba(30, 27, 75, 0.4)',
    borderLight: 'rgba(30, 27, 75, 0.05)',
  },
  fonts: {
    display: "'Faustina', Georgia, serif",
    body: "'DM Sans', sans-serif",
    mono: "'JetBrains Mono', monospace"
  }
};

const Terms = () => {
  const sections = [
    {
      icon: FileCheck,
      color: v5.colors.purple,
      title: 'Acceptance of Terms',
      content: 'By accessing and using Tappd\'s services, you agree to be bound by these Terms of Service. If you do not agree to these terms, please do not use our services.'
    },
    {
      icon: AlertCircle,
      color: v5.colors.orange,
      title: 'Offer Redemption',
      content: 'Promotional offers are subject to availability and business discretion. Each promo code can typically be used once per customer unless otherwise stated. Offers cannot be combined with other promotions unless explicitly permitted.'
    },
    {
      icon: CreditCard,
      color: v5.colors.teal,
      title: 'Subscriptions and Billing',
      content: 'Tappd plans are subscriptions for businesses that advertise with us. They are billed in advance by card through our payment processor, Stripe.',
      points: [
        'You choose monthly or annual billing. Monthly plans are charged once a month; annual plans are charged once a year, up front, for the full year.',
        'Your subscription renews automatically at the end of each billing period until you cancel it.',
        'You can cancel at any time. Cancellation takes effect at the end of the billing period you have already paid for — you keep your plan until then, and your deals are paused once it ends.',
        'We do not refund part of a billing period you have already paid for, except where the law requires us to.',
        'If we change the price of your plan, we will tell you at least 30 days before the change takes effect.',
        'If a payment fails, your deals are paused until you update your payment details. Once payment goes through, your deals go live again.',
      ]
    },
    {
      icon: Shield,
      color: v5.colors.teal,
      title: 'User Conduct',
      content: 'You agree to use Tappd services only for lawful purposes. Fraudulent use of promo codes or abuse of the system may result in account termination and legal action.'
    },
    {
      icon: Scale,
      color: v5.colors.amber,
      title: 'Disclaimer',
      content: 'Tappd acts as a marketing platform connecting customers with local businesses. We are not responsible for the quality of goods or services provided by participating businesses. All disputes should be resolved directly with the business.'
    },
    {
      icon: AlertCircle,
      color: v5.colors.red,
      title: 'Limitation of Liability',
      content: 'Tappd shall not be liable for any indirect, incidental, special, or consequential damages arising from your use of our services.'
    },
    {
      icon: RefreshCw,
      color: v5.colors.purple,
      title: 'Changes to Terms',
      content: 'We reserve the right to modify these terms at any time. Continued use of our services after changes constitutes acceptance of the modified terms.'
    },
    {
      icon: Mail,
      color: v5.colors.orange,
      title: 'Contact',
      content: 'Questions about these terms? Contact us at hello@tappd.app'
    }
  ];

  return (
    <div className="min-h-screen" style={{ backgroundColor: v5.colors.cream }}>
      <SEO
        title="Terms of Service"
        description="Tappd's terms of service outline the rules and guidelines for using our QR coaster advertising platform and promotional offers."
        url="/terms"
        noIndex={false}
      />
      {/* V5 Navy Hero Section */}
      <section
        className="relative overflow-hidden py-20"
        style={{ backgroundColor: v5.colors.navy, paddingTop: '8rem', boxShadow: '0 4px 20px rgba(30, 27, 75, 0.3)' }}
      >
        <div className="max-w-[920px] mx-auto px-6 text-center relative z-10">
          {/* Code Accent */}
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            style={{ fontFamily: v5.fonts.mono, fontSize: '12px', color: 'rgba(255, 255, 255, 0.4)', marginBottom: '16px' }}
          >
            // terms_of_service
          </motion.p>

          <motion.div
            initial={{ scale: 0, rotate: -180 }}
            animate={{ scale: 1, rotate: 0 }}
            transition={{ duration: 0.8, type: "spring", stiffness: 100 }}
            className="inline-block mb-6"
          >
            <div
              className="w-20 h-20 rounded-xl flex items-center justify-center mx-auto"
              style={{ backgroundColor: 'rgba(139, 92, 246, 0.2)' }}
            >
              <FileCheck size={40} style={{ color: v5.colors.white }} />
            </div>
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="mb-4"
            style={{
              fontFamily: v5.fonts.display,
              color: v5.colors.white,
              fontSize: 'clamp(2.5rem, 7vw, 4rem)',
              fontWeight: 500
            }}
          >
            Terms of Service
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            style={{
              fontFamily: v5.fonts.body,
              color: 'rgba(255, 255, 255, 0.6)',
              fontSize: '16px'
            }}
          >
            Last updated: {new Date().toLocaleDateString()}
          </motion.p>
        </div>

        {/* V5 Gradient Line */}
        <div className="absolute bottom-0 w-full h-1" style={{
          background: `linear-gradient(to right, ${v5.colors.purple} 0%, ${v5.colors.orange} 100%)`
        }} />
      </section>

      {/* Content Section */}
      <section className="py-16">
        <div className="max-w-[920px] mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.6 }}
            className="space-y-6"
          >
            {sections.map((section, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: idx * 0.1 }}
              >
                <div
                  className="rounded-xl p-8"
                  style={{
                    backgroundColor: v5.colors.white,
                    border: `1px solid ${v5.colors.borderLight}`,
                    boxShadow: '0 2px 4px rgba(30, 27, 75, 0.04)',
                    borderLeft: `4px solid ${section.color}`
                  }}
                >
                  <div className="flex items-start gap-6">
                    <motion.div
                      whileHover={{ rotate: 360, scale: 1.1 }}
                      transition={{ duration: 0.6 }}
                      className="flex-shrink-0"
                    >
                      <div
                        className="w-14 h-14 rounded-xl flex items-center justify-center"
                        style={{ backgroundColor: `${section.color}15` }}
                      >
                        <section.icon size={28} style={{ color: section.color }} />
                      </div>
                    </motion.div>
                    <div className="flex-1">
                      <h2 style={{
                        fontFamily: v5.fonts.display,
                        fontSize: '24px',
                        fontWeight: 500,
                        color: v5.colors.textPrimary,
                        marginBottom: '12px'
                      }}>
                        {section.title}
                      </h2>
                      <p style={{
                        fontFamily: v5.fonts.body,
                        fontSize: '16px',
                        color: v5.colors.textSecondary,
                        lineHeight: '1.7'
                      }}>
                        {section.content}
                      </p>
                      {section.points && (
                        <ul
                          className="mt-4 space-y-3 list-disc pl-5"
                          style={{
                            fontFamily: v5.fonts.body,
                            fontSize: '16px',
                            color: v5.colors.textSecondary,
                            lineHeight: '1.7'
                          }}
                        >
                          {section.points.map((point, pointIdx) => (
                            <li key={pointIdx}>{point}</li>
                          ))}
                        </ul>
                      )}
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* V5 Bottom Gradient */}
      <div className="h-1" style={{
        background: `linear-gradient(to right, ${v5.colors.purple} 0%, ${v5.colors.orange} 100%)`
      }} />
    </div>
  );
};

export default Terms;
